import {
	BookOpen,
	Calculator,
	Check,
	ChevronDown,
	Copy,
	ExternalLink,
	Hash,
	Wrench,
	type LucideIcon,
} from 'lucide-react'
import {
	createContext,
	Fragment,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import {
	safeHref,
	type Inline,
	type Ruta,
	type RutaBlock,
	type RutaFooter,
	type RutaIcon,
} from '#app/utils/rutor.ts'

/**
 * Assistentens renderare för informationsrutor (kontraktet: app/utils/rutor.ts).
 * Ren presentation: tar färdiga rutor som props och ritar blocktyperna - inga
 * beroenden på assistentens chattbibliotek. Känner inte till lathundar, koder
 * eller kalkylatorer; en ny rutsort eller ändrat innehåll kräver ingen ändring
 * här. Okända block, ikoner och layouter ritas neutralt (kontraktets punkt 5).
 *
 * - <RutaView>: en ruta som block (markör på egen rad).
 * - <RutaLinkView>: en ruta som länk i löptext (markör mitt i en mening).
 * - <RutorFooterView>: raderna under svaret.
 */

const ICONS: Record<RutaIcon, LucideIcon> = {
	book: BookOpen,
	hash: Hash,
	calculator: Calculator,
	wrench: Wrench,
}
const iconOf = (icon: RutaIcon): LucideIcon => ICONS[icon] ?? Wrench

const GREEN = 'text-green-700 dark:text-green-400'
const linkProps = { target: '_blank', rel: 'noopener noreferrer' } as const

// Utseende ("ljus", samma skal som Fass-rutorna): vit ruta med mörkgrön
// vänsterkant, IM:s brödtext och dämpade text i grönt i stället för appens
// blågrå muted-ton.
const BRAND_TEXT =
	'text-green-950 dark:text-foreground [&_.text-muted-foreground]:text-green-900/70 dark:[&_.text-muted-foreground]:text-muted-foreground'
const BOX = `not-prose my-4 overflow-hidden rounded-xl border border-green-200/80 border-l-4 border-l-green-600 bg-white dark:border-border dark:border-l-green-600 text-[13px] leading-relaxed shadow-sm dark:bg-background ${BRAND_TEXT}`
const BADGE =
	'shrink-0 whitespace-nowrap rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700 ring-1 ring-green-200 dark:bg-transparent dark:text-green-400'
const PILL_LINK =
	'inline-flex items-center gap-1 rounded-full border border-green-200 bg-white px-2.5 py-0.5 text-xs font-medium text-green-700 no-underline transition-colors hover:border-green-400 hover:bg-green-50 dark:border-border dark:bg-transparent dark:text-green-400'

/**
 * Vad användaren gjorde i en ruta, för ytans statistik. `part` säger vilken
 * del: kod (kodformat) eller text i en cell, lank (länkknapp), bild, ruta
 * (hela rutan är länken, t.ex. kalkylatorn). `rowCode` = koden på samma
 * tabellrad, så att ytan kan skilja t.ex. ICD från KVÅ även för benämningen.
 */
export type RutaPart = 'kod' | 'text' | 'lank' | 'bild' | 'ruta'
export type RutaAction =
	| { type: 'copy'; part: RutaPart; rowCode?: string }
	| {
			type: 'open'
			href: string
			part: RutaPart
			rowCode?: string
			/** Raden under svaret: vilket verktyg länken går till. */
			icon?: RutaIcon
	  }
	| { type: 'expand'; what: 'visa_hela' | 'details' }

/**
 * Valfria återanrop från rutorna till ytan som ritar dem (t.ex. statistik).
 * Renderaren känner inte till hur de används; utan provider händer inget.
 */
export const RutaActionsContext = createContext<{
	onAction?: (action: RutaAction) => void
}>({})

const useRutaAction = () => useContext(RutaActionsContext).onAction

/** Ett återanrop som bara anropas första gången (kopiera/fäll ut räknas en gång per element). */
function useOnce<A extends unknown[]>(fn: ((...args: A) => void) | undefined) {
	const done = useRef(false)
	return (...args: A) => {
		if (done.current || !fn) return
		done.current = true
		fn(...args)
	}
}

export function RutaView({ ruta }: { ruta: Ruta }) {
	if (ruta.layout === 'link') return <LinkRuta ruta={ruta} />
	return (
		<div className={BOX} data-ruta={ruta.id}>
			<div className="flex items-start justify-between gap-3 px-4 pb-2 pt-3">
				<RutaTitle ruta={ruta} withSource />
				{ruta.source && (
					<span className={`hidden sm:inline-flex ${BADGE}`}>
						{ruta.source}
					</span>
				)}
			</div>
			<div className="px-4 pb-3">
				<RutaBody ruta={ruta} />
			</div>
		</div>
	)
}

/** Markör mitt i en mening: rutans titel som länk till verktyget. */
export function RutaLinkView({ ruta }: { ruta: Ruta }) {
	const Icon = iconOf(ruta.icon)
	const href = safeHref(ruta.href)
	const onAction = useRutaAction()
	const content = (
		<>
			<Icon className="h-3 w-3" />
			{ruta.title}
			{href && <ExternalLink className="h-3 w-3" />}
		</>
	)
	const cls =
		'mx-0.5 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 align-baseline text-[12px] font-medium leading-5 text-green-800 no-underline hover:border-green-400 dark:border-border dark:bg-transparent dark:text-green-400'
	return href ? (
		<a
			href={href}
			{...linkProps}
			className={cls}
			data-ruta-link={ruta.id}
			onClick={() => onAction?.({ type: 'open', href, part: 'ruta' })}
		>
			{content}
		</a>
	) : (
		<span className={cls} data-ruta-link={ruta.id}>
			{content}
		</span>
	)
}

/** Ikon i rund platta, rubrik i två nivåer: vad det är (överrad), vilket. */
function RutaTitle({
	ruta,
	withSource,
}: {
	ruta: Ruta
	/** Mobil: källmärkningen i överraden i stället för som etikett. */
	withSource?: boolean
}) {
	const Icon = iconOf(ruta.icon)
	return (
		// Rubriken radbryts hellre än kapas (smala skärmar).
		<div className="flex min-w-0 items-center gap-2.5">
			<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600 ring-1 ring-green-100 dark:bg-transparent dark:text-green-400 dark:ring-border">
				<Icon className="h-4 w-4" />
			</span>
			<div className="min-w-0">
				{(ruta.subtitle || (withSource && ruta.source)) && (
					<p className="text-[11px] font-semibold uppercase leading-tight tracking-wide text-green-700 dark:text-green-400">
						{ruta.subtitle}
						{withSource && ruta.source && (
							<span className="font-medium text-green-900/60 dark:text-muted-foreground sm:hidden">
								{ruta.subtitle ? ' · ' : ''}
								{ruta.source}
							</span>
						)}
					</p>
				)}
				<p className="break-words text-[15px] font-semibold leading-snug text-green-900 dark:text-foreground">
					{ruta.title}
				</p>
			</div>
		</div>
	)
}

/** Rutans innehåll; långa rutor visas avklippta med "Visa hela". */
function RutaBody({ ruta }: { ruta: Ruta }) {
	const [all, setAll] = useState(false)
	const expanded = useOnce(useRutaAction())
	const clipped = !!ruta.collapse && !all
	return (
		<div className="space-y-2.5">
			<div
				className={`space-y-2.5 ${clipped ? 'relative max-h-80 overflow-hidden' : ''}`}
			>
				<Blocks blocks={ruta.blocks} />
				{clipped && (
					<div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent dark:from-background" />
				)}
			</div>
			{clipped && (
				<button
					type="button"
					onClick={() => {
						setAll(true)
						expanded({ type: 'expand', what: 'visa_hela' })
					}}
					className="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 ring-1 ring-green-200 hover:bg-green-100 dark:bg-transparent dark:text-green-400 dark:ring-border"
				>
					Visa hela
					<ChevronDown className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	)
}

/** Hela rutan är en länk (kalkylatorer). */
function LinkRuta({ ruta }: { ruta: Ruta }) {
	const href = safeHref(ruta.href)
	const onAction = useRutaAction()
	if (!href) return null
	return (
		<a
			href={href}
			{...linkProps}
			className={`${BOX} group flex flex-col gap-2.5 px-4 py-3 no-underline transition-colors hover:border-green-300 hover:border-l-green-600 hover:bg-green-50/40 dark:hover:bg-transparent sm:flex-row sm:items-center sm:gap-4`}
			data-ruta={ruta.id}
			onClick={() => onAction?.({ type: 'open', href, part: 'ruta' })}
		>
			<span className="min-w-0 flex-1">
				<RutaTitle ruta={ruta} />
				{ruta.description && (
					<span className="mt-1.5 line-clamp-2 block text-muted-foreground sm:pl-[42px]">
						{ruta.description}
					</span>
				)}
			</span>
			{ruta.linkLabel && (
				<span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full bg-green-600 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors group-hover:bg-green-700">
					{ruta.linkLabel}
					<ExternalLink className="h-3.5 w-3.5" />
				</span>
			)}
		</a>
	)
}

// ---------------------------------------------------------------------------
// Block

function InlineView({
	inline,
	rowCode,
}: {
	inline: Inline
	/** Koden på samma tabellrad (för statistiken). */
	rowCode?: string
}) {
	const onAction = useRutaAction()
	return (
		<>
			{inline.map((s, i) => {
				const cls = [
					s.bold ? 'font-medium' : '',
					s.mono ? `font-mono ${GREEN}` : '',
					'whitespace-pre-line',
				]
					.filter(Boolean)
					.join(' ')
				const href = safeHref(s.href)
				const part: RutaPart = s.mono ? 'kod' : 'text'
				const content = href ? (
					<a
						href={href}
						{...linkProps}
						className={`${cls} hover:underline`}
						onClick={() => onAction?.({ type: 'open', href, part, rowCode })}
					>
						{s.text}
					</a>
				) : (
					<span className={cls}>{breakAtSlashes(s.text)}</span>
				)
				// Knappen följer texten: ordbindningen (U+2060) håller den kvar efter
				// sista ordet, så den bryts aldrig ensam till en ny rad. select-none
				// håller tecknet och knappen utanför en manuell markering, så att en
				// kopierad tabell blir som förut (tabbar, inga osynliga tecken).
				return s.copyable ? (
					<span key={i}>
						{content}
						<span className="select-none whitespace-nowrap">
							{'\u2060'}
							<CopyButton text={s.text} part={part} rowCode={rowCode} />
						</span>
					</span>
				) : (
					<Fragment key={i}>{content}</Fragment>
				)
			})}
		</>
	)
}

/**
 * Kopierar t.ex. en diagnoskod eller benämning till journalen. Bocken (och
 * tooltip/skärmläsartext) visar att det gick; ingen synlig etikett, som annars
 * gör kolumnen bredare och får tabellen att hoppa.
 */
function CopyButton({
	text,
	part,
	rowCode,
}: {
	text: string
	part: RutaPart
	rowCode?: string
}) {
	const copiedOnce = useOnce(useRutaAction())
	const [copied, setCopied] = useState(false)
	const timer = useRef<ReturnType<typeof setTimeout>>()
	const Icon = copied ? Check : Copy
	// Rensa timern om rutan försvinner (t.ex. när svaret genereras om).
	useEffect(() => () => clearTimeout(timer.current), [])
	return (
		<>
			<button
				type="button"
				onClick={async () => {
					if (await copyText(text)) {
						copiedOnce({ type: 'copy', part, rowCode })
						setCopied(true)
						// Flera snabba klick: bara det senaste släcker bocken.
						clearTimeout(timer.current)
						timer.current = setTimeout(() => setCopied(false), 1500)
					}
				}}
				aria-label={copied ? `${text} kopierad` : `Kopiera ${text}`}
				title={copied ? 'Kopierad' : 'Kopiera'}
				className={`ml-1 inline-flex items-center rounded p-1.5 align-middle ${copied ? 'bg-green-100 text-green-800 dark:bg-muted' : 'text-green-700/60'} transition-colors hover:bg-green-100 hover:text-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600/50 dark:text-green-400/70 dark:hover:bg-muted`}
			>
				<Icon className="h-3.5 w-3.5" />
			</button>
			{/* Skärmläsare: en ändrad aria-label på fokuserad knapp läses inte pålitligt,
			    och barn till en knapp ignoreras - därför ett syskon. */}
			<span role="status" className="sr-only">
				{copied ? 'Kopierad' : ''}
			</span>
		</>
	)
}

/** Urklipp; utan Clipboard API (äldre webbläsare, icke-säker kontext) via ett dolt fält. */
async function copyText(text: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch {
		try {
			const el = document.createElement('textarea')
			el.value = text
			el.setAttribute('readonly', '')
			el.style.position = 'fixed'
			el.style.opacity = '0'
			// select() flyttar fokus; lämna tillbaka det till knappen efteråt.
			const focused = document.activeElement as HTMLElement | null
			document.body.appendChild(el)
			el.select()
			const ok = document.execCommand('copy')
			el.remove()
			focused?.focus()
			return ok
		} catch {
			return false
		}
	}
}

/**
 * Radbrytningsmöjlighet efter snedstreck: "skada/slaganfall/…" i en smal
 * tabellcell spräcker annars rutan på mobil. <wbr> följer inte med vid kopiering.
 */
function breakAtSlashes(text: string): ReactNode {
	if (!text.includes('/')) return text
	return text.split('/').map((part, i) => (
		<Fragment key={i}>
			{i > 0 && (
				<>
					/<wbr />
				</>
			)}
			{part}
		</Fragment>
	))
}

function Blocks({ blocks }: { blocks: RutaBlock[] }) {
	return (
		<>
			{blocks.map((b, i) => (
				<Block key={i} b={b} />
			))}
		</>
	)
}

function Block({ b }: { b: RutaBlock }) {
	const onAction = useRutaAction()
	switch (b.type) {
		case 'text':
			return (
				<p className={b.muted ? 'text-muted-foreground' : ''}>
					<InlineView inline={b.content} />
				</p>
			)
		case 'heading':
			return (
				<p className="pt-1 font-semibold text-green-900 dark:text-foreground">
					{b.text}
				</p>
			)
		case 'list': {
			const items = b.items.filter(i => i.some(s => s.text.trim()))
			if (items.length === 0) return null
			const Tag = b.ordered ? 'ol' : 'ul'
			return (
				<Tag
					className={`space-y-1 pl-4 marker:text-green-600 ${b.ordered ? 'list-decimal' : 'list-disc'}`}
				>
					{items.map((it, i) => (
						<li key={i}>
							<InlineView inline={it} />
						</li>
					))}
				</Tag>
			)
		}
		case 'table':
			return (
				<div className="overflow-x-auto rounded-lg border border-green-100 dark:border-border">
					<table className="w-full border-collapse text-left">
						<tbody>
							{b.rows.map((r, i) => {
								// Rubrikrad (bara rubrikceller) är grön. Rubrikceller i en
								// datarad (t.ex. HUSK MIDAS bokstäver) är bara feta och följer
								// radens randning, så att raden går att följa.
								const headRow = r.cells.every(c => c.header)
								const rowCode = r.cells
									.flatMap(c => c.content)
									.find(s => s.mono)?.text
								return (
									<tr
										key={i}
										className={`border-b border-green-100 last:border-b-0 dark:border-border ${r.highlight ? 'bg-green-100/70 dark:bg-muted' : headRow ? 'bg-green-50 dark:bg-muted' : 'even:bg-gray-100/80 dark:even:bg-white/[0.04]'}`}
									>
										{r.cells.map((cell, j) => {
											const Tag = cell.header ? 'th' : 'td'
											return (
												<Tag
													key={j}
													colSpan={cell.colSpan}
													rowSpan={cell.rowSpan}
													className={`px-2.5 py-1 align-top leading-snug ${cell.header ? 'font-semibold text-green-900 dark:text-foreground' : ''} ${!headRow && (cell.rowSpan ?? 1) > 1 ? 'bg-white dark:bg-background' : ''}`}
												>
													<InlineView inline={cell.content} rowCode={rowCode} />
												</Tag>
											)
										})}
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			)
		case 'image': {
			const src = safeHref(b.src)
			if (!src) return null
			const target = safeHref(b.href) ?? src
			return (
				<a
					href={target}
					{...linkProps}
					onClick={() =>
						onAction?.({
							type: 'open',
							href: target,
							part: 'bild',
						})
					}
				>
					<img
						src={src}
						alt={b.alt}
						loading="lazy"
						className="max-h-72 max-w-full rounded-lg border border-green-100 bg-white"
					/>
				</a>
			)
		}
		case 'links':
			return (
				<div className="flex flex-wrap gap-1.5 border-t border-green-100 pt-2.5 dark:border-border">
					{b.links.map(l => {
						const href = safeHref(l.href)
						return href ? (
							<a
								key={href + l.label}
								href={href}
								{...linkProps}
								className={PILL_LINK}
								onClick={() => onAction?.({ type: 'open', href, part: 'lank' })}
							>
								{l.label}
								<ExternalLink className="h-3 w-3" />
							</a>
						) : null
					})}
				</div>
			)
		case 'details':
			return <DetailsBlock b={b} />
		default:
			// Okänd blocktyp från en nyare server: ingenting (kontraktets punkt 5).
			return null
	}
}

function DetailsBlock({ b }: { b: Extract<RutaBlock, { type: 'details' }> }) {
	const opened = useOnce(useRutaAction())
	return (
		// En länkknapp som försvinner när avsnittet fällts ut; bara den egna
		// <summary> döljs (inte nästlade avsnitts).
		<details
			className="[&[open]>summary]:hidden"
			onToggle={e => {
				if (e.currentTarget.open) opened({ type: 'expand', what: 'details' })
			}}
		>
			<summary
				className={`w-fit cursor-pointer select-none list-none text-xs font-medium ${GREEN} hover:underline [&::-webkit-details-marker]:hidden`}
			>
				{b.summary}
			</summary>
			<div className="mt-1 space-y-1.5">
				<Blocks blocks={b.blocks} />
			</div>
		</details>
	)
}

// ---------------------------------------------------------------------------
// Under svaret

export function RutorFooterView({ footer }: { footer: RutaFooter[] }) {
	const onAction = useRutaAction()
	return (
		<>
			{footer
				.filter(f => f.type === 'links')
				.map(f => {
					const Icon = iconOf(f.icon)
					return (
						<div
							key={f.id}
							className={`mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-green-200/80 bg-white px-3.5 py-2 text-[13px] shadow-sm dark:border-border dark:bg-background ${BRAND_TEXT}`}
							data-ruta-footer={f.id}
						>
							<span className="mr-1 inline-flex items-center gap-1.5 font-semibold text-green-900 dark:text-foreground">
								<Icon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
								{f.title}
							</span>
							{f.items.map(item => {
								const ItemIcon = iconOf(item.icon)
								const href = safeHref(item.href)
								return href ? (
									<a
										key={href + item.label}
										href={href}
										{...linkProps}
										className={PILL_LINK}
										onClick={() =>
											onAction?.({
												type: 'open',
												href,
												part: 'lank',
												icon: item.icon,
											})
										}
									>
										<ItemIcon className="h-3 w-3" />
										{item.label}
										<ExternalLink className="h-3 w-3" />
									</a>
								) : null
							})}
						</div>
					)
				})}
		</>
	)
}
